import Raven from 'raven-js';
import { useCallback, useMemo } from 'react';
import { useGetCopilotQuery, useListCopilotsSummariesQuery } from '../../../../../__generated__/chirp/com/hubspot/copilot/rpc/CopilotCustomCopilotsService.hooks';
import { useHasChatAgentAccess, useHasSalesCopilotAccess } from '../../../../gating/hooks/useHasAccess';
import { useIsUngatedForBreezeRebrand } from '../../../../gating/hooks/useIsUngatedForBreezeRebrand';
import { useHostAppContext } from '../../../../hostAppContext/hooks/useAppContext';
import { SALES_COPILOT_ASSISTANT, getBreezeCopilotAssistant } from '../constants/assistants';
import { transformCopilotSummaryToAssistant, transformCopilotToCopilotSummary } from '../utils/assistantsTypeTransforms';
const transformToAssistants = ({
  copilots,
  draftCopilot,
  hasSalesCopilotAccess,
  hasChatAgentAccess,
  initialCustomCopilotId,
  isPreviewMode,
  isUngatedForBreezeRebrand
}) => {
  // Adds BREEZE as a default assistant
  const assistants = [getBreezeCopilotAssistant(isUngatedForBreezeRebrand)];
  if (hasSalesCopilotAccess) {
    assistants.push(SALES_COPILOT_ASSISTANT);
  }
  if (hasChatAgentAccess) {
    /** We only want to show published custom copilots
     * If we are in preview mode, we want to show the draft custom copilot
     * at the top of the list
     */
    const publishedCustomCopilots = copilots.filter(copilot => copilot.status === 'ACTIVE');
    // We only want to add the draft custom copilot if it isn't already in publishedCustomCopilots
    const isDraftCustomCopilotPublished = publishedCustomCopilots.some(copilot => copilot.id === initialCustomCopilotId);
    if (!isDraftCustomCopilotPublished && initialCustomCopilotId && isPreviewMode) {
      if (draftCopilot) {
        publishedCustomCopilots.unshift(transformCopilotToCopilotSummary(draftCopilot));
      }
    }
    const chatAgents = publishedCustomCopilots.map(transformCopilotSummaryToAssistant);
    // Type assertion to ensure the transformed agents match the Assistant type
    assistants.push(...chatAgents);
  }
  return assistants;
};
export const useAssistants = () => {
  const isUngatedForBreezeRebrand = useIsUngatedForBreezeRebrand();
  const {
    customCopilotId: initialCustomCopilotId,
    isPreviewMode
  } = useHostAppContext();
  const hasSalesCopilotAccess = useHasSalesCopilotAccess();
  const hasChatAgentAccess = useHasChatAgentAccess();
  const {
    loading: loadingCustomCopilots,
    data: {
      copilots: customCopilots
    } = {
      copilots: []
    },
    refetch: refetchCustomCopilots
  } = useListCopilotsSummariesQuery({
    skip: !hasChatAgentAccess
  });

  // List Copilot only displays active custom assistants, this will allow us to get the draft custom copilot
  const {
    loading: loadingDraftCustomCopilot,
    data: {
      copilot: draftCustomCopilot
    } = {},
    refetch: refetchDraftCustomCopilot
  } = useGetCopilotQuery({
    variables: {
      customCopilotId: Number(initialCustomCopilotId)
    },
    skip: !initialCustomCopilotId || !isPreviewMode
  });
  const refetchAssistants = useCallback(async () => {
    try {
      const [customCopilotsResponse, draftCustomCopilotResponse] = await Promise.all([refetchCustomCopilots(), refetchDraftCustomCopilot()]);
      return transformToAssistants({
        copilots: customCopilotsResponse.data.result.copilots,
        draftCopilot: draftCustomCopilotResponse.data.result.copilot,
        hasSalesCopilotAccess,
        hasChatAgentAccess,
        initialCustomCopilotId,
        isPreviewMode,
        isUngatedForBreezeRebrand
      });
    } catch (error) {
      Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
        extra: {
          component: 'useAssistants',
          action: 'refetchAssistants',
          context: 'Failed to refetch assistants data from CHIRP services',
          hasInitialCustomCopilotId: !!initialCustomCopilotId,
          isPreviewMode,
          hasChatAgentAccess
        }
      });
      throw error; // Re-throw for caller to handle
    }
  }, [hasChatAgentAccess, hasSalesCopilotAccess, initialCustomCopilotId, isPreviewMode, isUngatedForBreezeRebrand, refetchCustomCopilots, refetchDraftCustomCopilot]);
  const assistants = useMemo(() => {
    if (!customCopilots) {
      return [];
    }
    return transformToAssistants({
      copilots: customCopilots,
      draftCopilot: draftCustomCopilot,
      hasSalesCopilotAccess,
      hasChatAgentAccess,
      initialCustomCopilotId,
      isPreviewMode,
      isUngatedForBreezeRebrand
    });
  }, [customCopilots, draftCustomCopilot, hasChatAgentAccess, hasSalesCopilotAccess, initialCustomCopilotId, isPreviewMode, isUngatedForBreezeRebrand]);
  return {
    assistants,
    loading: loadingCustomCopilots || loadingDraftCustomCopilot,
    refetchAssistants
  };
};